/**
 * Custom SVG Radar Chart component
 * Precise positioning: A=135°, B=225°, C=315°, D=45°
 */
import { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { ThinkingScores } from '../types';

export interface CustomRadarChartProps {
  scores: ThinkingScores;
  title?: string;
  width?: number;
  height?: number;
  showLabels?: boolean;
  color?: string;
}

interface Point {
  x: number;
  y: number;
}

export const CustomRadarChart: React.FC<CustomRadarChartProps> = ({
  scores,
  title,
  width = 300,
  height = 300,
  showLabels = true,
  color,
}) => {
  const theme = useTheme();
  const chartColor = color || theme.palette.primary.main;

  const chartData = useMemo(() => {
    const scoresMap = scores.scores;

    // Define angles in mathematical coordinates (degrees)
    const angles: Record<string, number> = {
      A: 135, // Upper left / northwest
      B: 225, // Lower left / southwest
      C: 315, // Lower right / southeast
      D: 45,  // Upper right / northeast
    };

    // Type order for drawing: D → A → B → C (forms proper shape)
    const typeOrder = ['D', 'A', 'B', 'C'];

    const maxValue = Math.max(...Object.values(scoresMap), 1);

    return typeOrder.map(type => ({
      type,
      value: scoresMap[type] || 0,
      angle: angles[type],
      maxValue,
    }));
  }, [scores]);

  // SVG dimensions
  const center = width / 2;
  const maxRadius = (Math.min(width, height) / 2) * 0.8;

  // Convert polar coordinates (angle in degrees, radius) to cartesian (x, y)
  const polarToCartesian = (angle: number, radius: number): Point => {
    const angleRad = (angle * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(angleRad),
      y: center - radius * Math.sin(angleRad), // SVG y-axis is inverted
    };
  };

  // Generate concentric circles (grid)
  const gridLevels = 5;
  const gridCircles = Array.from({ length: gridLevels }, (_, i) => {
    const radius = (maxRadius / gridLevels) * (i + 1);
    return radius;
  });

  // Generate axis lines
  const axisLines = chartData.map(({ type, angle }) => {
    const end = polarToCartesian(angle, maxRadius);
    return { type, end };
  });

  // Generate data polygon
  const dataPoints = chartData.map(({ value, angle, maxValue }) => {
    const radius = (value / maxValue) * maxRadius;
    return polarToCartesian(angle, radius);
  });

  const polygonPoints = dataPoints.map(p => `${p.x},${p.y}`).join(' ');

  // Label positions (slightly outside the max radius)
  const labels = chartData.map(({ type, angle }) => {
    const labelRadius = maxRadius + 20;
    const pos = polarToCartesian(angle, labelRadius);
    return { type, ...pos };
  });

  return (
    <Box
      data-testid="custom-radar-chart"
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {title && (
        <Typography variant="h6" component="h3" gutterBottom>
          {title}
        </Typography>
      )}
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ overflow: 'visible' }}
      >
        {/* Grid circles */}
        {gridCircles.map((radius, i) => (
          <circle
            key={i}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={theme.palette.divider}
            strokeWidth="1"
          />
        ))}

        {/* Axis lines */}
        {axisLines.map(({ type, end }) => (
          <line
            key={type}
            x1={center}
            y1={center}
            x2={end.x}
            y2={end.y}
            stroke={theme.palette.divider}
            strokeWidth="1"
          />
        ))}

        {/* Data polygon */}
        <polygon
          points={polygonPoints}
          fill={chartColor}
          fillOpacity={0.5}
          stroke={chartColor}
          strokeWidth="2"
        />

        {/* Data points */}
        {dataPoints.map((point, i) => (
          <circle
            key={i}
            cx={point.x}
            cy={point.y}
            r="4"
            fill={chartColor}
            stroke="#fff"
            strokeWidth="2"
          />
        ))}

        {/* Labels */}
        {showLabels && labels.map(({ type, x, y }) => (
          <text
            key={type}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={theme.palette.text.primary}
            fontSize="14"
            fontWeight="500"
          >
            {type}
          </text>
        ))}
      </svg>
    </Box>
  );
};
